'use client'

import { useNavigate } from 'react-router-dom'
import { useFormContext } from 'react-hook-form'
import { Button, Navigation, Spacer } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  BusinessRegistrationModel,
  clickEvent,
} from '@marketplace-web/business-accounts/business-accounts-data'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

import { ORDERED_REGISTRATION_STEPS, RegistrationStep } from '../../../constants'
import { ROUTES } from '../../../constants/routes'
import { OnValidateRegistrationParams } from '../hooks/useUpdateRegistration'
import { OnSubmitRegistrationParams } from '../hooks/useSubmitRegistration'

type Props = {
  stepIndex: number
  loading: boolean
  onBackButtonClick?: () => void
  onUpdateRegistration: ({
    registrationFields,
    validate,
    onSuccess,
  }: OnValidateRegistrationParams) => Promise<void>
  onSubmitRegistration: ({
    registrationFields,
    onSuccess,
  }: OnSubmitRegistrationParams) => Promise<void>
}

const screenByStep = {
  [RegistrationStep.BusinessDetails]: 'registration_business_details',
  [RegistrationStep.BusinessRepresentative]: 'registration_business_representative',
  [RegistrationStep.BusinessProfile]: 'registration_business_profile',
}

const FormNavigation = ({
  stepIndex,
  loading,
  onBackButtonClick,
  onUpdateRegistration,
  onSubmitRegistration,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('business.registration.action')
  const navigate = useNavigate()

  const screens = screenByStep
  const registrationSteps = ORDERED_REGISTRATION_STEPS

  const { handleSubmit, getValues } = useFormContext<Partial<BusinessRegistrationModel>>()

  const isFirstStep = stepIndex === 0
  const isLastStep = stepIndex === registrationSteps.length - 1

  function redirectToPreviousStep() {
    const previousStep = registrationSteps[stepIndex - 1]

    navigate(`${ROUTES.REGISTER.BASE}/${previousStep!}`)
  }

  function redirectToNextStep() {
    if (isLastStep) {
      navigate(ROUTES.WALLET_CONVERSION)
    } else {
      const nextStep = registrationSteps[stepIndex + 1]

      navigate(`${ROUTES.REGISTER.BASE}/${nextStep!}`)
    }
  }

  async function handleBackButtonClick() {
    if (isFirstStep) return

    track(
      clickEvent({
        target: 'business_registration_back',
        screen: screens[registrationSteps[stepIndex]!],
      }),
    )

    const registrationFields = getValues()

    await onUpdateRegistration({
      registrationFields,
      validate: false,
    })
    redirectToPreviousStep()
    onBackButtonClick?.()
  }

  async function handleSubmitClick() {
    const registrationFields = getValues()
    if (isLastStep) {
      await onSubmitRegistration({ registrationFields, onSuccess: redirectToNextStep })
    } else {
      await onUpdateRegistration({
        registrationFields,
        validate: true,
        onSuccess: redirectToNextStep,
      })
    }
  }

  function handleActionClick(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (loading) {
      event.preventDefault()

      return
    }

    handleSubmit(handleSubmitClick)()
  }

  function renderRightActions() {
    return (
      <div className="u-flexbox u-justify-content-flex-end">
        <SeparatedList separator={<Spacer orientation={Spacer.Orientation.Vertical} />}>
          {!isFirstStep && (
            <Button
              size={Button.Size.Medium}
              text={translate('back')}
              onClick={handleBackButtonClick}
              inline
            />
          )}
          <Button
            size={Button.Size.Medium}
            styling={Button.Styling.Filled}
            text={translate(isLastStep ? 'submit' : 'continue')}
            onClick={handleActionClick}
            isLoading={loading}
            testId="business-accounts--continue-button"
            inline
          />
        </SeparatedList>
      </div>
    )
  }

  return (
    <div className="container u-ui-padding-vertical-medium">
      <Navigation right={renderRightActions()} />
    </div>
  )
}

export default FormNavigation
